import { createTheme, CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import { createContext, useContext } from 'react';
import { StringUtils } from '../utils/StringUtils';

export const KrThemeContext = createContext();

const baseThemeOptions = {
  typography: { fontFamily: '"Manrope"' },
  shape: { borderRadius: 16 },
  components: {
    MuiFormControl: {
      defaultProps: {
        fullWidth: true,
        variant: 'filled',
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'filled',
        fullWidth: true,
        slotProps: {
          inputLabel: { shrink: true },
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '3px',
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: Number(theme.shape.borderRadius) * 2,
          backgroundColor: theme.palette.surface.high,
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.surface.high,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.surface.main,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.surface.high,
        }),
      },
    },
    MuiPickersLayout: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          backgroundColor: theme.palette.surface.light,
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2, 3),
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '9999px',
          boxShadow: 'none',
          textTransform: 'none',
          padding: theme.spacing(0.5, 3),
          fontSize: '1rem',
          fontWeight: 500,
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        // Overrides for the Tabs indicator can be specified here.
        indicator: ({ theme }) => ({
          backgroundColor: theme.palette.primary.dark, // Darker underline
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          fontWeight: 500,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            // Light background on hover
          },
          '&.Mui-selected': {
            color: theme.palette.primary.dark,
            fontWeight: 600,
          },
        }),
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }) => ({
          // Add a light border using the divider color from the theme
          overflow: 'hidden', // ensure inner buttons don't leak corners
          borderRadius: theme.shape.borderRadius, // round the whole group
          border: `1px solid ${theme.palette.divider}`, // add the border

          // Increase horizontal padding (px) to make buttons wider.
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
          },
          '& .Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          },
        }),
      },
    },
  },
};

let lightPrimary = '#3F51B5';
let theme = createTheme({
  ...baseThemeOptions,
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: lightPrimary,
          contrastText: '#ffffff',
        },
        secondary: {
          main: '#565c84',
        },
        background: {
          default: '#fbf8ff',
        },
        surface: {
          main: '#fbf8ff',
          light: '#ffffff',
          high: '#EBE9F4',
          highest: '#E2E0EB',
          low: '#F6F4FD',
          lowest: '#ffffff',
          container: '#EFEDF6',
          dark: '#D8D6E1',
          tint: '#4355b9',
          variant: '#E2E1F1',
        },

        error: {
          main: '#ef5350',
          light: '#ffd7db',
          dark: '#a73a38',
        },
        warning: {
          main: '#f6c343',
          light: '#F8E5A7',
          dark: '#b38a00',
        },
        success: {
          main: '#58c96a',
          light: '#b6e8be',
          dark: '#3a8f49',
        },
      },
    },
    dark: {
      palette: {
        primary: {
          main: '#BAC3FF',
        },
        secondary: {
          main: '#BEC4F2',
        },
        background: {
          default: '#0D0E14',
        },
        text: {
          primary: '#E3E1EA',
          secondary: '#C5C5D4',
        },

        surface: {
          main: '#0D0E14',
          light: '#34343B',
          high: '#1F1F26',
          highest: '#292930',
          low: '#121319',
          lowest: '#08090F',
          container: '#1A1B22',
          dark: '#0D0E14',
          tint: '#BAC3FF',
          variant: '#3A3B43',
        },
      },
    },
  },
});

theme = createTheme(theme, {
  color: (color) => {
    if (!color) return null;
    for (let part of color.split('.')) {
      color = theme.palette[part] || color;
    }
    if (typeof color === 'object') {
      color = color.main || color.default || color[500] || null;
    }
    return StringUtils.toValidColor(color); // validate color
  },
});

export const KrThemeProvider = ({ children }) => {
  const globalStyles = (
    <GlobalStyles
      styles={(theme) => ({
        body: { fontFamily: theme.typography.fontFamily, backgroundColor: theme.palette.background.default },
        '.ag-status-bar': {
          border: 'none',
        },
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '*::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.divider,
          borderRadius: '10px',
        },
      })}
    />
  );

  return (
    <KrThemeContext.Provider value={{}}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {globalStyles}
        {children}
      </ThemeProvider>
    </KrThemeContext.Provider>
  );
};

export const useKrTheme = () => useContext(KrThemeContext);
